import instance, { ADMIN_PREFIX } from 'services/api';
import {
  FastAppointmentDetail,
  FastAppointmentListQuery,
  FastAppointmentListResponse,
  FastAppointmentListRow,
  UpdateFastAppointmentStatusInput
} from 'types/api/fastAppointments';

interface PaginatedEnvelope<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

/**
 * GET /api/v2/admin/fast-appointments (cycle-27) — paginated list with
 * name / status / organizationId filters. PaginatedResponse envelope
 * (1-based page); service coerces to {results,total} (0-based FE).
 */
export const fetchFastAppointments = async (
  query: FastAppointmentListQuery
): Promise<FastAppointmentListResponse> => {
  const params: Record<string, string | number> = {
    page: query.page ?? 1,
    limit: query.limit ?? 10
  };

  if (query.name) params.name = query.name;
  if (query.status) params.status = query.status;
  if (query.organizationId != null)
    params.organizationId = query.organizationId;

  const res = await instance.get<PaginatedEnvelope<FastAppointmentListRow>>(
    `${ADMIN_PREFIX}/fast-appointments`,
    { params }
  );

  return {
    results: res.data.data ?? [],
    total: res.data.total ?? 0
  };
};

/**
 * GET /api/v2/admin/fast-appointments/:id — detail with notes[] history
 * hydrated for the FE Timeline.
 */
export const fetchFastAppointmentById = async (
  id: string | number
): Promise<FastAppointmentDetail> => {
  const res = await instance.get<FastAppointmentDetail>(
    `${ADMIN_PREFIX}/fast-appointments/${id}`
  );

  return res.data;
};

/**
 * PUT /api/v2/admin/fast-appointments/:id/status — append a history
 * row + flip the status. Returns the hydrated detail.
 */
export const updateFastAppointmentStatus = async (
  id: string | number,
  input: UpdateFastAppointmentStatusInput
): Promise<FastAppointmentDetail> => {
  const res = await instance.put<FastAppointmentDetail>(
    `${ADMIN_PREFIX}/fast-appointments/${id}/status`,
    input
  );

  return res.data;
};

/**
 * PUT /api/v2/admin/fast-appointments/:id/organization — reassign org.
 */
export const updateFastAppointmentOrganization = async (
  id: string | number,
  organizationId: number
): Promise<FastAppointmentDetail> => {
  const res = await instance.put<FastAppointmentDetail>(
    `${ADMIN_PREFIX}/fast-appointments/${id}/organization`,
    { organizationId }
  );

  return res.data;
};
